import React, { useState } from 'react';
import { DataTable } from '../components/DataTable';
import { StatusBadge } from '../components/StatusBadge';
import { reports } from '../data/mockData';
import { Download } from 'lucide-react';
import { toast } from 'sonner';

export default function Reports() {
  const [selectedReport, setSelectedReport] = useState(null);

  const handleDownload = (report) => {
    // Simulate DOCX file generation and download
    const content = `
SECURITY AUDIT REPORT
=====================

Report ID: ${report.id}
Client: ${report.client}
Risk Rating: ${report.riskRating.toUpperCase()}
Date: ${report.date}

EXECUTIVE SUMMARY
-----------------
${report.summary}

FINDINGS
--------
This report contains detailed findings from the security audit conducted on ${report.date}.
All vulnerabilities have been categorized by severity and include remediation recommendations.

RECOMMENDATIONS
---------------
1. Address all critical vulnerabilities immediately
2. Implement security patches for high-priority issues
3. Review and update security policies
4. Conduct follow-up assessment in 30 days

---
Generated by SecureOps Platform
`;
    
    const blob = new Blob([content], { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${report.id}_Security_Report.docx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    
    toast.success(`Report ${report.id} downloaded successfully`);
  };

  const columns = [
    { key: 'id', label: 'Report ID' },
    { key: 'client', label: 'Client' },
    { 
      key: 'riskRating', 
      label: 'Risk Rating',
      render: (row) => <StatusBadge severity={row.riskRating} />
    },
    { key: 'date', label: 'Date' },
    {
      key: 'actions',
      label: 'Actions',
      render: (row) => (
        <div className="action-buttons">
          <button 
            className="action-btn btn-primary"
            onClick={() => setSelectedReport(row)}
            data-testid={`preview-report-${row.id}`}
          >
            Preview
          </button>
          <button 
            className="action-btn btn-success"
            onClick={() => handleDownload(row)}
            data-testid={`download-report-${row.id}`}
          >
            <Download size={16} />
          </button>
        </div>
      )
    }
  ];

  return (
    <div className="page-container" data-testid="reports-page">
      <div className="page-header">
        <h1 className="page-title">Reports</h1>
        <p className="page-subtitle">Security audit reports and summaries</p>
      </div>

      <div className="reports-layout">
        <div className="reports-table">
          <DataTable 
            columns={columns} 
            data={reports}
            searchable={true}
            emptyMessage="No reports available"
          />
        </div>

        {selectedReport && (
          <div className="report-preview" data-testid="report-preview-panel">
            <h3 className="preview-title">Report Preview</h3>
            <div className="preview-content">
              <div className="preview-field">
                <span className="preview-label">Report ID:</span>
                <span className="preview-value">{selectedReport.id}</span>
              </div>
              <div className="preview-field">
                <span className="preview-label">Client:</span>
                <span className="preview-value">{selectedReport.client}</span>
              </div>
              <div className="preview-field">
                <span className="preview-label">Risk Rating:</span>
                <StatusBadge severity={selectedReport.riskRating} />
              </div>
              <div className="preview-field">
                <span className="preview-label">Date:</span>
                <span className="preview-value">{selectedReport.date}</span>
              </div>
              <div className="preview-summary">
                <h4 className="summary-title">Summary</h4>
                <p className="summary-text">{selectedReport.summary}</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
